import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useLocation } from 'react-router-dom'
import TenancySwitcher from './TenancySwitcher'
import { useSlugNavigate } from '../hooks/useNavigation'

const DashboardLayout = ({ children, variant = 'default' }) => {
  const { user, logout, getActiveSlug } = useAuth()
  const navigate = useSlugNavigate()
  const location = useLocation()
  const slug = getActiveSlug()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef(null)

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === `/${slug}`
    }
    return location.pathname === `/${slug}${path}`
  }

  const isSettingsActive = (path) => {
    if (path === '/settings') {
      return location.pathname === `/${slug}/settings`
    }
    return location.pathname.startsWith(`/${slug}${path}`)
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar Navigation */}
      <aside className="flex w-64 flex-col bg-white border-r border-[#dbe0e6]">
        <div className="flex flex-col h-full p-4 gap-6">
          {/* Logo Section */}
          <div className="flex items-center gap-3 px-2 py-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined text-2xl">menu_book</span>
            </div>
            <div className="flex flex-col">
              <h1 className="text-[#111418] text-base font-bold leading-normal">Twinbooks</h1>
              <p className="text-[#617589] text-xs font-normal leading-normal uppercase tracking-wider">
                {user?.tenancies?.find(t => t.id === user.active_tenancy_id)?.name || 'Partner Portal'}
              </p>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col gap-1">
            {variant === 'settings' ? (
              <>
                <a
                  onClick={() => navigate('/settings')}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    isSettingsActive('/settings')
                      ? 'bg-primary text-white'
                      : 'text-[#111418] hover:bg-gray-100'
                  }`}
                >
                  <span className="material-symbols-outlined text-[22px]">person</span>
                  <p className="text-sm font-semibold">Profile</p>
                </a>
                <a
                  onClick={() => navigate('/settings/organizations')}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    isSettingsActive('/settings/organizations')
                      ? 'bg-primary text-white'
                      : 'text-[#111418] hover:bg-gray-100'
                  }`}
                >
                  <span className="material-symbols-outlined text-[22px]">business</span>
                  <p className="text-sm font-medium">Organizations</p>
                </a>
              </>
            ) : (
              <>
                <a
                  onClick={() => navigate('/')}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    isActive('/')
                      ? 'bg-primary text-white'
                      : 'text-[#111418] hover:bg-gray-100'
                  }`}
                >
                  <span className="material-symbols-outlined text-[22px]">dashboard</span>
                  <p className="text-sm font-semibold">Dashboard</p>
                </a>
                <a
                  onClick={() => navigate('/migrations')}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    isActive('/migrations')
                      ? 'bg-primary text-white'
                      : 'text-[#111418] hover:bg-gray-100'
                  }`}
                >
                  <span className="material-symbols-outlined text-[22px]">sync_alt</span>
                  <p className="text-sm font-medium">Migrations</p>
                </a>
              </>
            )}
          </nav>

          {/* Bottom Profile Section */}
          <div className="mt-auto border-t border-[#dbe0e6] pt-4 px-2 relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 w-full hover:bg-gray-50 rounded-lg p-2 transition-colors"
            >
              <div className="bg-primary/20 rounded-full h-10 w-10 flex items-center justify-center text-primary font-bold">
                {user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex flex-col flex-1 min-w-0 text-left">
                <p className="text-[#111418] text-sm font-bold truncate">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email?.split('@')[0] || 'User'}
                </p>
                {user?.tenancies?.find(t => t.id === user.active_tenancy_id)?.name && (
                  <p className="text-[#617589] text-xs truncate">
                    {user.tenancies.find(t => t.id === user.active_tenancy_id).name}
                  </p>
                )}
              </div>
              <span className="material-symbols-outlined text-[#617589] text-[20px]">
                {showUserMenu ? 'expand_more' : 'expand_less'}
              </span>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-[#dbe0e6] py-2 mx-2">
                {/* Tenancy Switcher */}
                {user?.tenancies && user.tenancies.length > 0 && (
                  <>
                    <div className="py-2">
                      <TenancySwitcher
                        tenancies={user.tenancies}
                        activeTenancyId={user.active_tenancy_id}
                      />
                    </div>
                    <div className="border-t border-[#dbe0e6] my-1"></div>
                  </>
                )}

                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    navigate('/settings')
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[#617589] text-[20px]">settings</span>
                  <span className="text-sm font-medium text-[#111418]">Settings</span>
                </button>
                <div className="border-t border-[#dbe0e6] my-1"></div>
                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    logout()
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[#617589] text-[20px]">logout</span>
                  <span className="text-sm font-medium text-[#111418]">Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-y-auto bg-[#f6f7f8]">
        {children}
      </main>
    </div>
  )
}

export default DashboardLayout
